---
title: Integrations
lede: Connect GitHub, Jira, Trello, a bookmarklet and ngrok, one step at a time.
order: 34
part: Plan the work
---

Integrations connect Airlock to the places your work comes from. Open **Settings › Add an integration**, at the foot of the Integrations group.

![The integrations Airlock offers](media/settings-integrations.webp)

### Setting one up

Each integration is a row with one button: **Connect** to start (**Set up** for the bookmarklet), **Resume** to finish one left half done or whose credential was refused, **Open** once it works. Its chip reads `Step 2 of 4` until it reads **In place**. The last row installs [the command line](/command-line).

Each integration you start gets its own row in the settings rail, with a dot that says whether it works. **Remove this integration**, at the foot of its page, takes it out after asking once more. Every credential is kept in the OS keychain and never enters a sandbox.

### GitHub

1. **The account** — **Connect** opens your browser and asks for a one-time code. This is Airlock's own sign-in, not the `gh` you may have installed.
2. **Pull requests** — **Turn them on** to start sessions from pull requests and push merges back. See [Pull requests](/pull-requests).

Once in place, the page lists your repositories whose remote is on GitHub. A workspace can turn pull requests off for itself in **Workspace settings › Git & merge**.

### Jira and Trello

Tickets assigned to you (Trello: cards you are on) arrive on [the board](/board), in **Backlog**.

1. Jira asks for **The account**, numbered on one page:
   1. **Your Jira site**: the address you open Jira at, such as `acme.atlassian.net`. Pasting the whole URL is fine.
   2. **Your Atlassian account**: the email you sign in to Atlassian with.
   3. **Create an API token on id.atlassian.com**: **Open id.atlassian.com** opens [id.atlassian.com/manage-profile/security/api-tokens](https://id.atlassian.com/manage-profile/security/api-tokens). There, press **Create API token**, name it `Airlock`, choose when it expires, press **Create**, then **Copy**. Atlassian shows it once.
   4. **Paste the token here**.
   5. **Check the connection**: **Check and connect** saves the site and asks Jira whether the token works for that email. A refusal is said under the token.

   Trello asks for **The key and the token** together.
2. **Where tickets land** (Trello: **Where cards land**) maps each Jira project or Trello board to one workspace. Tickets from a project you did not map are not imported.
3. Jira only, optional: **Acceptance criteria** names the field your site keeps a ticket's acceptance criteria in, by its id (`customfield_10035`). A pull request review compares the diff with them when the ticket's description has no *Acceptance criteria* section — see [Pull requests](/pull-requests#what-the-pull-request-says).

Each mapping also sets:

| Setting | What it does |
|---|---|
| **Statuses to import** (Trello: lists) | Which statuses create a card. Empty imports every open one |
| **Status that closes a ticket** | Closing a session offers to move its ticket there. **Never move it** sends nothing |
| **Lanes and statuses** | The status each lane stands for: **Backlog**, **To do**, **In development**, **Waiting**, **Archived** |

### A sign-in shared by your organization

An organization can share one Jira sign-in and one Trello sign-in with its members. An owner or an administrator sets it under **Shared by your organization**, on the Jira or Trello page: the account email and API token (Trello: the API key and token), then **Share with** *your organization*. **Replace it** changes it, **Stop sharing** takes it back. It is sealed on that computer before it leaves; the account area never sees it.

A member with no sign-in of their own on this computer uses it, and their own always wins. The page then says whose shared sign-in it is. Under a shared Jira sign-in, each member imports the tickets assigned to their own address on the site; Trello does not show a member's address, so each member imports the mapped boards' open cards.

### How the sync moves things

Lanes and statuses move both ways.

- Moving a card to a lane, by hand or by the board, moves its ticket to that lane's status within seconds.
- Moving a ticket in Jira or Trello back to the **Backlog** status moves its card from **To do** to **Backlog**. A ticket moves its card nowhere else: a ticket moved to **To do** does not start a session, since its text was written by whoever can edit the ticket. Dragging the card is what starts it.
- When both sides moved since the last pass, the later move wins. A lane left **Not synced** moves nothing either way.
- A card whose ticket's status is a different stage from its lane (in progress in Jira, still in **Backlog** here) says so on the card. The stage is read from the lane you mapped that status to, else from Jira's status category, whatever the status is called.

### What you add to a card

A card's title and text are the ticket's: each pass rewrites them, so the task form shows them read only. What you write in the form goes in your notes, under the ticket's text and its rule. The sync never touches it, and the agent reads it after the ticket's text as your own instructions.

New tickets are looked for every 10 minutes; **The pass** on the integration's page changes it (2 to 120 minutes). **Sync tickets** on the board, or **Sync** on the page, runs a pass now. Removing Jira or Trello forgets the credential and the mappings; imported cards stay on the board and stop following their ticket.

### Bookmarklet

The bookmarklet turns a page open in your browser (a ticket, an error, a log) into a task.

1. Drag **Investigate in Airlock** to your bookmarks bar, or use **Copy bookmarklet** and paste it as a bookmark's address.
2. On any page, select the part that matters (or nothing) and click the bookmark.

The task form opens on the page's text and images. If the text holds a stack trace, its frames are matched to files in your repositories, and the form opens on the workspace that holds them, asking the agent to find the cause and fix it. Removing the bookmarklet only takes it off the rail; delete the bookmark to stop it working.

To do the same from a terminal or a Node script, with files as well, use [the command line](/command-line).

### Stack traces from the clipboard

**File › Investigate clipboard…** (`⌘⌥N`) does the same from a stack trace or a report you copied. Java, Node, Python, Go, Rust, Ruby and .NET traces are read; frames from the platform or dependencies are ignored.

### ngrok

ngrok gives a port of a session a public https URL, so a webhook, a sign-in callback or another device can reach the work in progress. Its page walks you through it on one page:

1. **Create a free ngrok account** with **Sign up on ngrok** (skip it if you have one).
2. **Copy your authtoken**: **Open authtoken page** opens [dashboard.ngrok.com/get-started/your-authtoken](https://dashboard.ngrok.com/get-started/your-authtoken); press **Copy** there.
3. **Paste it here**.
4. **Keep it**. Its shape is checked at once; ngrok checks the token itself when the first tunnel opens.
5. **Optional: a domain that stays the same**. Claim a domain with **Open ngrok domains** (one is free) and enter it as the repository's **Tunnel domain**, in **Workspace settings › Repositories**, so the URL stays the same across sessions.

Then turn **Public URLs** on. See [Tunnels](/tunnels).
